import React, { useEffect, useMemo, useState } from "react";
import "../styles/pages/BulkOrderPage.css";
import "../styles/pages/AccountPageSpacing.css";
import "../styles/shared/LoadingState.css";
import { toast } from "../utils/toast";
import { submitBulkOrder } from "../services/supportService";
import { BulkInquiry } from "../types/store";
import { MobilePageHeader } from "../components/layout/MobileAppChrome";
import CategorySection from "../components/home/CategorySection";
import "../styles/pages/MobileMarketplace.css";
import "../styles/pages/HomePage.css";
import { getCategories } from "../services/productService";
import { CategorySummary } from "../types/store";
import { useAuth } from "../contexts/AuthContext";
import { normalizeIndianMobileNumber } from "../utils/phoneNumber";

const WHATSAPP_SUPPORT_NUMBER = "919347023242";

interface BulkOrderFormState {
  name: string;
  mobileNumber: string;
  email: string;
  productCategories: string[];
  requirements: string;
  priorityRequest: boolean;
}

type BulkOrderErrors = Partial<Record<keyof BulkOrderFormState, string>>;

const BulkOrderPage: React.FC = () => {
  const { user } = useAuth();
  const [submitted, setSubmitted] = useState<BulkInquiry | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationPopupOpen, setValidationPopupOpen] = useState(false);
  const [categories, setCategories] = useState<CategorySummary[]>([]);
  const [fieldErrors, setFieldErrors] = useState<BulkOrderErrors>({});
  const [userEditedFields, setUserEditedFields] = useState({
    name: false,
    mobileNumber: false,
  });
  const [formState, setFormState] = useState<BulkOrderFormState>({
    name: "",
    mobileNumber: "",
    email: "",
    productCategories: [],
    requirements: "",
    priorityRequest: false,
  });

  const categoryOptions = useMemo(() => categories, [categories]);
  const selectedCategories = useMemo(
    () =>
      categoryOptions.filter((category) => {
        const categoryKey = category.slug ?? category.name;
        return formState.productCategories.includes(categoryKey);
      }),
    [categoryOptions, formState.productCategories],
  );

  useEffect(() => {
    if (!user) {
      return;
    }

    setFormState((current) => {
      const nextName = user.fullName?.trim() ? user.fullName.trim() : current.name;
      const nextMobileNumber = user.phoneNumber?.trim()
        ? normalizeIndianMobileNumber(user.phoneNumber)
        : current.mobileNumber;

      return {
        ...current,
        name: userEditedFields.name || current.name.trim() ? current.name : nextName,
        mobileNumber: userEditedFields.mobileNumber || current.mobileNumber.trim() ? current.mobileNumber : nextMobileNumber,
      };
    });
  }, [user, userEditedFields.name, userEditedFields.mobileNumber]);

  const buildValidationErrors = () => {
    const nextErrors: BulkOrderErrors = {};
    if (!formState.name.trim()) {
      nextErrors.name = "* required";
    }
    if (!formState.mobileNumber.trim()) {
      nextErrors.mobileNumber = "* required";
    }
    if (!formState.productCategories.length) {
      nextErrors.productCategory = "* required";
    }
    return nextErrors;
  };

  const buildWhatsAppMessage = () => {
    const lines = [
      "Bulk order inquiry from Eldoo",
      `Name: ${formState.name.trim()}`,
      `Mobile: ${formState.mobileNumber.trim()}`,
      formState.email.trim() ? `Email: ${formState.email.trim()}` : null,
      `Categories: ${selectedCategories.map((category) => category.name).join(", ") || formState.productCategories.join(", ")}`,
      formState.requirements.trim() ? `Requirements: ${formState.requirements.trim()}` : null,
      formState.priorityRequest ? "Priority: Urgent" : "Priority: Normal",
    ].filter((line): line is string => Boolean(line));

    return lines.join("\n");
  };

  const validateForm = () => {
    const nextErrors = buildValidationErrors();
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setValidationPopupOpen(true);
      toast.error("Please complete the required fields.");
      return false;
    }
    return true;
  };

  useEffect(() => {
    let cancelled = false;

    const loadCategories = async () => {
      try {
        const response = await getCategories();
        if (!cancelled) {
          setCategories(response);
        }
      } catch {
        if (!cancelled) {
          setCategories([]);
        }
      }
    };

    void loadCategories();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await submitBulkOrder({
        name: formState.name,
        mobileNumber: formState.mobileNumber,
        email: formState.email || undefined,
        productCategory: selectedCategories.map((category) => category.name).join(", "),
        requirements: formState.requirements,
        priorityRequest: formState.priorityRequest,
      });
      setSubmitted(response);
      toast.success("Bulk inquiry submitted");
    } catch {
      toast.error("Unable to submit bulk inquiry right now.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsAppSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const whatsappMessage = buildWhatsAppMessage();
    const whatsappUrl = `https://wa.me/${WHATSAPP_SUPPORT_NUMBER}?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = event.target;
    if (type === "checkbox") {
      setFormState((current) => ({
        ...current,
        [name]: (event.target as HTMLInputElement).checked,
      }));
      return;
    }
    if (name === "name" || name === "mobileNumber") {
      setUserEditedFields((current) => ({
        ...current,
        [name]: true,
      }));
    }
    setFormState((current) => ({ ...current, [name]: value }));
  };

  const closeValidationPopup = () => {
    setValidationPopupOpen(false);
  };

  return (
    <section className="shell section page-section bulk-order-page">
      <MobilePageHeader title="Bulk Order Inquiry" />
      <div className="bulk-mobile-banner"><div><strong>Get better prices</strong><b>for large requirements</b><span>✓ Best Wholesale Prices</span><span>✓ Priority Support</span><span>✓ Timely Delivery</span></div><span className="bulk-mobile-banner__boxes">▦</span></div>
      <div className="page-header">
        <span className="eyebrow">Bulk Orders</span>
        <h1>Send your bulk requirement</h1>
      </div>

      <div className="bulk-layout">
        <form className="store-card form-card" onSubmit={handleSubmit}>
          <h2>Bulk inquiry form</h2>
          <div className="form-grid">
            <label>
              Full name
              <input name="name" value={formState.name} onChange={handleChange} required />
              {fieldErrors.name ? <small className="form-error">{fieldErrors.name}</small> : null}
            </label>
            <label>
              Mobile number
              <input name="mobileNumber" value={formState.mobileNumber} onChange={handleChange} required />
              {fieldErrors.mobileNumber ? <small className="form-error">{fieldErrors.mobileNumber}</small> : null}
            </label>
            <label className="form-grid__wide">
              Email (optional)
              <input name="email" type="email" value={formState.email} onChange={handleChange} />
            </label>
          </div>

          <section className="bulk-category-picker">
            <div className="bulk-category-picker__heading">
              <span>Category type</span>
              <small>
                {selectedCategories.length
                  ? `Selected: ${selectedCategories.map((category) => category.name).join(", ")}`
                  : "Pick one or more categories"}
              </small>
              {fieldErrors.productCategory ? <small className="form-error">{fieldErrors.productCategory}</small> : null}
            </div>
            <CategorySection
              categories={categoryOptions}
              title="Category type"
              showTitle={false}
              showAction={false}
              className="home-category-section"
              gridClassName="mobile-reference-home__category-grid bulk-order-page__category-grid"
              selectedCategoryKeys={formState.productCategories}
              onCategoryClick={(category) =>
                setFormState((current) => {
                  const categoryKey = category.slug ?? category.name;
                  const exists = current.productCategories.includes(categoryKey);
                  return {
                    ...current,
                    productCategories: exists
                      ? current.productCategories.filter((item) => item !== categoryKey)
                      : [...current.productCategories, categoryKey],
                  };
                })
              }
            />
          </section>
          <div className="form-grid">
            <label className="form-grid__wide">
              Requirements (optional)
              <textarea
                name="requirements"
                rows={5}
                value={formState.requirements}
                onChange={handleChange}
                placeholder="Describe product names, quantity, delivery needs, budget, or any other requirement."
              />
              {fieldErrors.requirements ? <small className="form-error">{fieldErrors.requirements}</small> : null}
            </label>
            <label className="checkout-inline-check">
              <input
                type="checkbox"
                name="priorityRequest"
                checked={formState.priorityRequest}
                onChange={handleChange}
              />
              <span>Mark this inquiry as urgent</span>
            </label>
          </div>

          <div className="bulk-order-actions">
            <button className="button" type="submit" disabled={submitting}>
              {submitting ? (
                <span className="button-loading">
                  <span className="button-loading__spinner" aria-hidden="true" />
                  Submitting...
                </span>
              ) : (
                "Submit"
              )}
            </button>
            <button className="button bulk-order-actions__whatsapp" type="button" onClick={handleWhatsAppSubmit}>
              Submit to WhatsApp
            </button>
          </div>
          {submitted ? (
            <p className="form-success">Thanks. Your bulk inquiry has been captured for follow-up.</p>
          ) : null}
        </form>

        <aside className="store-card support-card">
          <h2>What happens next</h2>
          <ul>
            <li>Admin receives your name, mobile number, optional email, category type, and requirement.</li>
            <li>Your selected category is attached to the inquiry for faster review.</li>
            <li>The team reviews availability and pricing before contacting you.</li>
            <li>Urgent inquiries are marked clearly in the admin panel.</li>
          </ul>
        </aside>
      </div>

      {validationPopupOpen ? (
        <div className="bulk-order-validation-modal" role="presentation" onClick={closeValidationPopup}>
          <div
            className="bulk-order-validation-modal__card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bulk-order-validation-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="bulk-order-validation-title">Missing details</h2>
            <p>Please fill all the details and select the category before submitting.</p>
            <button type="button" className="button" onClick={closeValidationPopup}>
              OK
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default BulkOrderPage;
